import axios from "axios";
import {
  DownloadFileCommand,
  FileData,
  GetUserCommand,
  LoginCommand,
  LoginToken,
  UploadFilesCommand,
  UserItem,
  AddJobCommand,
  UpdateJobCommand,
  ScrubSummary,
  PanderCountItem,
  Organization,
  EditOrganizationCommand,
  EditClientCommand,
  EditMailingPermitCommand,
  Client,
  AddProductCommand,
  Product,
  BasicResponse,
  AddOrganizationCommand,
  OrgLogoCommandDto,
} from "./models/schema";
import { config } from "../config";

export async function loginApi(
  bearerToken: string,
  loginCommand: LoginCommand
): Promise<LoginToken | undefined> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/user/login`;
  const response = await axios.post(baseUrl + endpoint, loginCommand, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY
    }
  });
  return response.data as LoginToken;
}

export async function getUserApi(
  bearerToken: string,
  userName: string
): Promise<UserItem | undefined> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/user`;
  const response = await axios.get(baseUrl + endpoint, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return response.data as UserItem;
}

export async function getUserOrgsApi(
  bearerToken: string,
  userName: string
): Promise<Organization[]> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization`;
  const response = await axios.get(baseUrl + endpoint, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return response.data as Organization[];
}

export async function getOrganizationApi(
  bearerToken: string,
  orgId: number,
  userName: string
): Promise<Organization> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}`;
  const response = await axios.get(baseUrl + endpoint, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return response.data as Organization;
}

export async function addOrganizationApi(
  bearerToken: string,
  addOrgCommand: AddOrganizationCommand,
  userName: string
): Promise<BasicResponse> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization`;
  const result = await axios.post(baseUrl + endpoint, addOrgCommand, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as BasicResponse;
}

export async function editOrganizationApi(
  bearerToken: string,
  orgId: number,
  editOrgCommand: EditOrganizationCommand,
  userName: string
): Promise<BasicResponse> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}`;
  const result = await axios.put(baseUrl + endpoint, editOrgCommand, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as BasicResponse;
}

export async function setOrgLogoApi(
  bearerToken: string,
  orgId: number,
  orgLogoCommand: OrgLogoCommandDto,
  userName: string
): Promise<BasicResponse> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/logo`;
  const result = await axios.put(baseUrl + endpoint, orgLogoCommand, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as BasicResponse;
}

export async function editOrgMailingPermitApi(
  bearerToken: string,
  orgId: number,
  permitId: number,
  editOrgPermitCommand: EditMailingPermitCommand,
  userName: string
): Promise<BasicResponse> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/mailingPermit/${permitId}`;
  const result = await axios.put(baseUrl + endpoint, editOrgPermitCommand, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as BasicResponse;
}

export async function addClientMailingPermitApi(
  bearerToken: string,
  orgId: number,
  clientId: number,
  editPermitCommand: EditMailingPermitCommand,
  userName: string
): Promise<BasicResponse> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/client/${clientId}/mailingPermit`;
  const result = await axios.post(baseUrl + endpoint, editPermitCommand, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as BasicResponse;
}

export async function addClientMailOwnerPermitApi(
  bearerToken: string,
  orgId: number,
  clientId: number,
  editPermitCommand: EditMailingPermitCommand,
  userName: string
): Promise<BasicResponse> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/client/${clientId}/mailOwnerPermit`;
  const result = await axios.post(baseUrl + endpoint, editPermitCommand, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as BasicResponse;
}

export async function editClientMailingPermitApi(
  bearerToken: string,
  orgId: number,
  mailingPermitId: number,
  editPermitCommand: EditMailingPermitCommand,
  userName: string,
  clientId?: number,
  mailOwnerClientId?: number
): Promise<BasicResponse> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/mailingPermit/${mailingPermitId}/${clientId ?? 0}/${mailOwnerClientId ?? 0}`;
  const result = await axios.put(baseUrl + endpoint, editPermitCommand, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as BasicResponse;
}

export async function addJobApi(
  bearerToken: string,
  orgId: number,
  clientId: number,
  addJobCommand: AddJobCommand,
  userName: string
): Promise<number> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/client/${clientId}/job`;
  const result = await axios.post(baseUrl + endpoint, addJobCommand, {
    timeout: 600000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.status as number;
}

export async function updateJobApi(
  bearerToken: string,
  orgId: number,
  clientId: number,
  jobId: number,
  updateJobCommand: UpdateJobCommand,
  userName: string
): Promise<number> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/pander/org/${orgId}/client/${clientId}/job/${jobId}`;
  const result = await axios.put(baseUrl + endpoint, updateJobCommand, {
    timeout: 150000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.status as number;
}

export async function uploadPanderFileApi(
  bearerToken: string,
  orgId: number,
  clientId: number,
  fileData: FileData,
  userName: string
): Promise<PanderCountItem> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/client/${clientId}/pander-file`;
  const response = await axios.post(baseUrl + endpoint, fileData, {
    timeout: 90000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return response.data as PanderCountItem;
}

export async function getClientApi(
  bearerToken: string,
  orgId: number,
  clientId: number,
  userName: string
): Promise<Client> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/client/${clientId}`;
  const response = await axios.get(baseUrl + endpoint, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return response.data as Client;
}

export async function addClientApi(
  bearerToken: string,
  orgId: number,
  addClientCommand: EditClientCommand,
  userName: string
): Promise<Organization> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/client`;
  const result = await axios.post(baseUrl + endpoint, addClientCommand, {
    timeout: 150000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as Organization;
}

export async function addProductApi(
  bearerToken: string,
  orgId: number,
  addProductCommand: AddProductCommand,
  userName: string
): Promise<Product> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/product`;
  const result = await axios.post(baseUrl + endpoint, addProductCommand, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as Product;
}

export async function editProductApi(
  bearerToken: string,
  orgId: number,
  productId: number,
  addProductCommand: AddProductCommand,
  userName: string
): Promise<Product> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/product/${productId}`;
  const result = await axios.put(baseUrl + endpoint, addProductCommand, {
    timeout: 15000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as Product;
}

export async function deleteProductApi(
  bearerToken: string,
  orgId: number,
  productId: number,
  userName: string
): Promise<Product> {
  const baseUrl = config.REACT_APP_PANDER_API_URL;
  const endpoint = `/api/organization/${orgId}/product/${productId}`;
  const result = await axios.delete(baseUrl + endpoint, {
    timeout: 150000,
    headers: {
      "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
      "UserName": userName,
    }
  });
  return result.data as Product;
}

export async function downloadJobAPI(token: string, jobId: string): Promise<Product>  {
    const baseUrl = config.REACT_APP_PANDER_API_URL;
    const endpoint = `/api/organization/product/${jobId}`;
    const result = await axios.get(baseUrl + endpoint, {
      timeout: 150000,
      headers: {
        "X-API-KEY": config.REACT_APP_PANDER_API_KEY,
        "UserName": 'admin',
      }
    });
    return result.data as Product;
}
